import { defineStore } from 'pinia'
import { ref } from 'vue'
import { Subscription } from '@/types/purchase'
import { useNotifyStore } from '@/stores/notifications'
import { uuid } from '@/composables'
import { STATUS_CODES } from '@/constants'
import { useAxios } from '@/composables/useAxios'
import { z } from 'zod'

export const useSubscriptionsStore = defineStore('subscriptions', () => {
  const subscriptions = ref<Subscription[]>([])

  /**
   * True once subscriptions were successfully fetched via
   * fetchSubscriptions() — distinguishes "empty account" from "not
   * loaded yet".
   */
  const loaded = ref(false)

  /** Shared in-flight promise so concurrent callers dedup to one request. */
  let inFlight: Promise<void> | null = null

  /**
   * Fetch subscriptions once and cache them. Skips the request when the
   * store already holds data (loaded, or populated by another fetcher);
   * pass `force: true` to refetch regardless. Errors propagate to the
   * caller.
   */
  async function fetchSubscriptions(force = false): Promise<void> {
    if (!force && (loaded.value || subscriptions.value.length > 0)) return
    if (inFlight && !force) return inFlight

    inFlight = (async () => {
      const res = await useAxios<null, Array<Subscription>>('GET', '/account/subs', true)
      if (res?.status === STATUS_CODES.OK) {
        const validate = z.array(Subscription).safeParse(res.data)
        if (validate.success) {
          subscriptions.value = validate.data
          loaded.value = true
        }
      }
    })().finally(() => {
      inFlight = null
    })
    return inFlight
  }

  function setSubscriptions(payload: Subscription[]) {
    const validate = z.array(Subscription).safeParse(payload)
    if (!validate.success) {
      const notifyStore = useNotifyStore()
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'error',
        title: 'Error',
        message: 'Payload needs to be an array of Subscription'
      })
      return
    }
    subscriptions.value = validate.data
  }

  function getSubscriptions(): Subscription[] {
    return subscriptions.value
  }

  return { subscriptions, loaded, fetchSubscriptions, getSubscriptions, setSubscriptions }
})
